import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { fhir2omopClient } from "../api/client";

export default function Dashboard() {
  const health = useQuery({
    queryKey: ["health"],
    queryFn: fhir2omopClient.health,
    refetchInterval: 30_000,
  });

  const caps = useQuery({
    queryKey: ["capabilities"],
    queryFn: fhir2omopClient.capabilities,
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-nexyo-dark">Dashboard</h1>
        <p className="text-nexyo-muted mt-1">
          Status, capabilities, and quick actions for the FHIR ↔ OMOP Mapper Service.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatusCard
          title="Service Status"
          value={health.isLoading ? "..." : health.data?.status ?? "unknown"}
          subtitle={health.data?.version ? `v${health.data.version}` : ""}
          variant={health.data?.status === "healthy" ? "success" : "danger"}
        />
        <StatusCard
          title="Active Jobs"
          value="0"
          subtitle="async backlog"
          variant="default"
        />
        <StatusCard
          title="Today's Conversions"
          value="0"
          subtitle="mock counter (Iteration 1)"
          variant="default"
        />
      </div>

      <section className="nx-card">
        <h2 className="font-semibold text-lg mb-4">Capabilities</h2>
        {caps.isLoading ? (
          <p className="text-nexyo-muted">Loading...</p>
        ) : caps.isError ? (
          <p className="text-nexyo-danger">Backend unreachable. Is docker-compose running?</p>
        ) : (
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <Field label="Transforms" values={caps.data?.transforms ?? []} />
            <Field label="Vocabularies" values={caps.data?.vocabularies ?? []} />
            <Field label="FHIR Profiles" values={caps.data?.fhir_profiles ?? []} />
            <Field
              label="Modes"
              values={[
                ...(caps.data?.modes ?? []),
                `sync ≤ ${caps.data?.max_payload_sync_mb ?? "?"} MB`,
                `async ≤ ${caps.data?.max_payload_async_gb ?? "?"} GB`,
              ]}
            />
            <Field
              label="EDPS integration"
              values={[
                `mode: ${caps.data?.edps_integration?.mode ?? "?"}`,
                `default: ${caps.data?.edps_integration?.default ?? "?"}`,
                `opt-in: ${caps.data?.edps_integration?.opt_in ?? "?"}`,
              ]}
            />
          </dl>
        )}
      </section>

      <section className="nx-card">
        <h2 className="font-semibold text-lg mb-4">Quick Actions</h2>
        <div className="flex flex-wrap gap-3">
          <Link to="/map" className="nx-button-primary">
            New Sync Mapping
          </Link>
          <button className="nx-button-secondary" disabled>
            New Batch Job (Iteration 2)
          </button>
          <button className="nx-button-secondary" disabled>
            Browse Audits (Iteration 2)
          </button>
        </div>
      </section>
    </div>
  );
}

interface StatusCardProps {
  title: string;
  value: string;
  subtitle: string;
  variant: "success" | "danger" | "default";
}

function StatusCard({ title, value, subtitle, variant }: StatusCardProps) {
  const colorClass =
    variant === "success"
      ? "text-nexyo-success"
      : variant === "danger"
      ? "text-nexyo-danger"
      : "text-nexyo-dark";

  return (
    <div className="nx-card">
      <p className="text-xs uppercase tracking-wide text-nexyo-muted">{title}</p>
      <p className={`text-3xl font-bold mt-2 ${colorClass}`}>{value}</p>
      <p className="text-sm text-nexyo-muted mt-1">{subtitle}</p>
    </div>
  );
}

function Field({ label, values }: { label: string; values: string[] }) {
  return (
    <div>
      <dt className="font-semibold text-nexyo-dark">{label}</dt>
      <dd className="text-nexyo-muted mt-1">
        <ul className="space-y-0.5">
          {values.map((v) => (
            <li key={v} className="font-mono text-xs">
              ▸ {v}
            </li>
          ))}
        </ul>
      </dd>
    </div>
  );
}
